import type { ExperienceDay, ExperienceItem, ExperienceSequence } from "@/lib/types";
import { CATEGORY_META } from "@/lib/experiences/categoryMeta";
import { isShowLocalReferencesEnabled } from "@/lib/experiences/launchFlags";
import { isMarketplaceEligibleProvider } from "./providerLinkState";
import { translate } from "@/lib/i18n/useI18n";
import type { Language } from "@/lib/i18n/languages";
import type { MessageKey } from "@/lib/i18n/messages";

export type RecommendationBadge = "top" | "good" | null;

// ─── Service-type display label ────────────────────────────────────────────
// Marketplace Grid Redesign + Provider Badges + Practical Filters V1 — a
// small, muted service_type label for anything that isn't a plain
// "experience" (transfer/airport_service/ticket/whatever future value the
// backend adds) — deliberately secondary to the provider badge. Falls back
// to the raw value title-cased rather than hiding an unrecognized future
// service_type. Relocated here (Comparador UX Redesign V1) so the card grid
// and the comparison drawer share the exact same label — never two
// independently-drifting copies of the same mapping.
const SERVICE_TYPE_LABEL_KEYS: Readonly<Record<string, MessageKey>> = {
  transfer:        "marketplace.serviceType.transfer",
  airport_service: "marketplace.serviceType.airportService",
  ticket:          "marketplace.serviceType.ticket",
};

// Sprint: Frontend UI i18n Foundation V1, Parte C — `language` is an
// OPTIONAL 2nd parameter defaulting to "es" (no existing test depends on
// the old signature, but the same backward-compatible convention this
// sprint uses everywhere else is kept for consistency).
export function serviceTypeLabel(serviceType: string | null | undefined, language: Language = "es"): string | null {
  const key = (serviceType ?? "").trim().toLowerCase();
  if (!key || key === "experience") return null;
  const messageKey = SERVICE_TYPE_LABEL_KEYS[key];
  return messageKey ? translate(language, messageKey) : (key.charAt(0).toUpperCase() + key.slice(1));
}

export interface MarketplaceItem {
  exp:          ExperienceItem;
  neighborhood: string;
  /** Stable unique key for selection tracking — the item's own backend id. */
  compositeId:  string;
  isSelected:   boolean;
  /** "top" → "Recomendada por RHEA", "good" → "Buen match", null → no badge. */
  recommendationBadge: RecommendationBadge;
}

// ─── Marketplace source resolution (Frontend Marketplace Integration V1) ───
// The backend's marketplace_inventory (Marketplace Supply Quality V2/V2.1/
// V2.2) is a flat, gate-filtered, ranked, deduped list — deeper and more
// marketplace-shaped than days[].experiences, which is capped by day-slot
// itinerary placement (~3 slots/day). Prefer it whenever present and
// non-empty; fall back to flattening days[] only for older/cached responses
// that predate the field. Never mixes the two — using marketplace_inventory
// means days[] is not also read, so nothing is ever duplicated.
export type MarketplaceSourceKind = "marketplace_inventory" | "days_fallback";

export interface MarketplaceSource {
  items:  ExperienceItem[];
  source: MarketplaceSourceKind;
}

function flattenDaysDeduped(days: ExperienceDay[]): ExperienceItem[] {
  const seen = new Set<string>();
  const items: ExperienceItem[] = [];
  for (const day of days) {
    for (const exp of day.experiences) {
      const dedupeKey = exp.id || `${exp.title}::${exp.provider_name ?? ""}`;
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      items.push(exp);
    }
  }
  return items;
}

export function getMarketplaceSource(sequence: ExperienceSequence): MarketplaceSource {
  const backendInventory = sequence.marketplace_inventory;
  const raw: MarketplaceSource = backendInventory && backendInventory.length > 0
    ? { items: backendInventory, source: "marketplace_inventory" }
    : { items: flattenDaysDeduped(sequence.days), source: "days_fallback" };

  if (isShowLocalReferencesEnabled()) return raw;

  // Provider-Only Marketplace & Comparison V2 Foundation, Part B — this is
  // the /experiences/sequence-based fallback path (used before the first
  // successful /experiences/search response, and permanently if it keeps
  // failing — task 6). The primary /experiences/search path already hides
  // local references server-side; filtering here too means the launch
  // marketplace never shows local content even while the fallback is
  // active. marketplace_inventory doesn't pass through the same final-
  // response eligibility filter as days[], so this also catches a provider
  // item that shouldn't have been marketplace-visible in the first place.
  return { items: raw.items.filter(isMarketplaceEligibleProvider), source: raw.source };
}

/**
 * provider_count / local_count for the current marketplace source. Prefers
 * the backend-computed counts (guaranteed to sum to marketplace_inventory
 * length) when marketplace_inventory is the active source; otherwise derives
 * from source_type client-side, matching the trust badge's own definition.
 *
 * When local references are hidden (the launch default), the backend's
 * sequence.provider_count/local_count describe the FULL, unfiltered
 * inventory — no longer accurate once getMarketplaceSource has already
 * filtered `items` down to eligible providers only, so counts are always
 * recomputed from `items` directly in that case.
 */
export function getMarketplaceCounts(
  sequence: ExperienceSequence,
  items:    ExperienceItem[],
  sourceKind: MarketplaceSourceKind,
): { providerCount: number; localCount: number } {
  if (!isShowLocalReferencesEnabled()) {
    const providerCount = items.filter(isMarketplaceEligibleProvider).length;
    return { providerCount, localCount: 0 };
  }
  if (
    sourceKind === "marketplace_inventory" &&
    typeof sequence.provider_count === "number" &&
    typeof sequence.local_count === "number"
  ) {
    return { providerCount: sequence.provider_count, localCount: sequence.local_count };
  }
  const providerCount = items.filter(e => e.source_type === "provider").length;
  return { providerCount, localCount: items.length - providerCount };
}

const TIME_OF_DAY_ORDER: Readonly<Record<string, number>> = {
  morning: 0, afternoon: 1, evening: 2, night: 3, any: 4,
};

/**
 * Ranks items by `rankItem` and assigns badges: the single strongest match
 * gets "top", the next two get "good", everyone else gets none. Items that
 * score 0 (no positive signal at all) never get a badge — an undifferentiated
 * catalog shouldn't produce a fake-feeling "recommendation". Ties broken by
 * original order, so badge placement is stable across renders.
 *
 * Recommendation Badge Coherence V1: `isIncompatible`, when provided, forces
 * a hard null override regardless of score or position — an item the user's
 * own query explicitly excluded (e.g. a Pub Crawl for "Madrid Segway tour")
 * must never carry "Recomendada por RHEA"/"Buen match", no matter how well
 * it otherwise scores on rating/availability/etc. The slot is never
 * backfilled by the next-best item — the brief only requires suppression,
 * and an item can still appear in the list unbadged when there's nothing
 * better available.
 */
function assignRecommendationBadges(
  items:          MarketplaceItem[],
  rankItem:       (exp: ExperienceItem) => number,
  isIncompatible?: (exp: ExperienceItem) => boolean,
): Map<string, RecommendationBadge> {
  const ranked = items
    .map((item, idx) => ({ item, idx, score: rankItem(item.exp) }))
    .sort((a, b) => b.score - a.score || a.idx - b.idx);

  const badges = new Map<string, RecommendationBadge>();
  ranked.forEach(({ item, score }, position) => {
    const badge: RecommendationBadge =
      score <= 0 ? null : position === 0 ? "top" : position <= 2 ? "good" : null;
    badges.set(item.compositeId, isIncompatible?.(item.exp) ? null : badge);
  });
  return badges;
}

/**
 * Builds the single marketplace inventory list — the "Todas las experiencias
 * disponibles" feed — from an already-flat, already-deduped item list (see
 * getMarketplaceSource above). Never groups by day/zone: marketplace_inventory
 * has no day concept, and the days_fallback path is deduped/flattened before
 * this function ever sees it, so this stays a pure list→list transform.
 *
 * Default ordering (used when `sortItems` is omitted, i.e. the user hasn't
 * picked a manual sort): RHEA recommendation rank when `rankItem` is
 * provided, falling back to provider-backed / reservable / time-of-day
 * otherwise. Stable, so unrelated items don't jitter between renders.
 *
 * Recommendation badges are always computed from `rankItem` (when provided)
 * — independent of the active manual sort — so badge placement doesn't
 * shuffle just because the user sorted by price or duration.
 *
 * Pass `sortItems` to defer to an explicit manual sort (duration/price/etc.)
 * instead of the default ordering above.
 *
 * `isIncompatible`, when provided, suppresses the recommendation badge (see
 * assignRecommendationBadges's own doc) — independent of `sortItems`/
 * `rankItem`, since it's about display honesty, not ordering.
 */
export function buildMarketplaceInventory(
  sourceItems:     ExperienceItem[],
  selectedIds:     Set<string>,
  sortItems?:      (exps: ExperienceItem[]) => ExperienceItem[],
  rankItem?:       (exp: ExperienceItem) => number,
  isIncompatible?: (exp: ExperienceItem) => boolean,
): MarketplaceItem[] {
  const rawItems: Omit<MarketplaceItem, "recommendationBadge">[] = sourceItems.map(exp => ({
    exp,
    neighborhood: exp.neighborhood || "",
    compositeId:  exp.id,
    isSelected:   selectedIds.has(exp.id),
  }));

  const items: MarketplaceItem[] = rawItems.map(item => ({ ...item, recommendationBadge: null }));
  if (rankItem) {
    const badges = assignRecommendationBadges(items, rankItem, isIncompatible);
    items.forEach(item => { item.recommendationBadge = badges.get(item.compositeId) ?? null; });
  }

  if (sortItems) {
    const sortedExps = sortItems(items.map(i => i.exp));
    const byId = new Map(items.map(i => [i.exp.id, i]));
    const reordered = sortedExps
      .map(exp => byId.get(exp.id))
      .filter((i): i is MarketplaceItem => !!i);
    if (reordered.length === items.length) return reordered;
    // Fallback safety net — shouldn't happen, but never drop items silently.
  }

  if (rankItem) {
    return items
      .map((item, idx) => ({ item, idx, score: rankItem(item.exp) }))
      .sort((a, b) => b.score - a.score || a.idx - b.idx)
      .map(({ item }) => item);
  }

  return items
    .map((item, idx) => ({ item, idx }))
    .sort((a, b) => {
      const pa = a.item.exp.source_type === "provider" ? 0 : 1;
      const pb = b.item.exp.source_type === "provider" ? 0 : 1;
      if (pa !== pb) return pa - pb;

      const ra = a.item.exp.availability_confirmed === true ? 0 : 1;
      const rb = b.item.exp.availability_confirmed === true ? 0 : 1;
      if (ra !== rb) return ra - rb;

      const ta = TIME_OF_DAY_ORDER[a.item.exp.time_of_day] ?? 4;
      const tb = TIME_OF_DAY_ORDER[b.item.exp.time_of_day] ?? 4;
      if (ta !== tb) return ta - tb;

      return a.idx - b.idx; // stable
    })
    .map(({ item }) => item);
}

// ─── Marketplace list rhythm (UX V3) ───────────────────────────────────────────
// The main list's own provider/local split (groupMarketplaceSections) was
// superseded by groupMarketplaceByIntent (Marketplace Inventory Sections
// V1, below) and removed. MIN_SECTION_SIZE/MarketplaceSection remain — the
// themed teaser (pickThemedTeaser) still uses both.

const MIN_SECTION_SIZE = 3;

export interface MarketplaceSection {
  /** Empty string = render with no header (sectioning wasn't meaningful). */
  label: string;
  items: MarketplaceItem[];
}

// ─── Intent-based grouping (Marketplace Inventory Sections V1) ─────────────
// A richer split than groupMarketplaceSections() above: instead of just
// provider-vs-local, this separates by what the item actually IS to the
// user — a city experience, an excursion/multi-day/multi-city trip, or a
// local/reference item — so the marketplace reads as "here's the city, here
// are side trips, here's local flavor" instead of one flat list. Supersedes
// groupMarketplaceSections() as the main list's grouping (that function is
// kept for any other caller that still wants the simpler 2-way split).
//
// marketplace_relevance_class (CORE_EXPERIENCE/DAY_TRIP/TRANSIT_LAYOVER_TOUR/
// etc.) is the backend's own classification — used when present — but it is
// a debug-only field (see lib/types.ts), empty on every normal production
// response. The title-based heuristic below is what actually does the work
// for real users; it deliberately mirrors (not imports — this is a display-
// only frontend concern, never authoritative) the same generic signals the
// backend's classifier uses: a multi-day count of 2+, the requested city
// named as the origin of a "desde/from" route, and multi-city phrasing. It
// does not need to be exhaustive — it only needs to catch the obvious,
// title-visible cases; anything it misses simply stays in cityExperiences,
// which is the safe default (never hides real inventory).

export interface MarketplaceIntentGroups {
  cityExperiences: MarketplaceItem[];
  excursions:      MarketplaceItem[];
  localReferences: MarketplaceItem[];
}

const _EXCURSION_RELEVANCE_CLASSES = new Set([
  "DAY_TRIP", "TRANSIT_LAYOVER_TOUR",
  // Defense-in-depth only — these should already be excluded from
  // marketplace_inventory server-side (Real Data Policy), never surfaced as
  // a recommendation, but if one ever slipped through a legacy days[]
  // fallback response, it belongs here, never in the primary city section.
  "TRANSPORT_LOGISTICS", "CITY_TO_CITY_TRANSFER", "LUGGAGE_STORAGE",
]);

const _MULTI_DAY_COUNT_PATTERN = /(?<![a-z0-9])(?:[2-9]|[1-9]\d+)\s*[-]?\s*(dias?|days?)(?![a-z])/i;
// Marketplace Section Classification Hotfix — confirmed production leak:
// "Tour de cuatro días a Toronto Montreal Ottawa Quebec para grupos
// pequeños" used a written-out day count, which _MULTI_DAY_COUNT_PATTERN
// (digits only) never matches. "un"/"one" deliberately excluded — a single
// day stays the legitimate in-city full-day-tour shape, same rule as the
// numeric pattern above. Text is already accent-folded before this runs
// (see _foldForMatch), so only the unaccented "dias"/"días"→"dias" form
// needs to appear here.
const _WRITTEN_MULTI_DAY_PATTERN = /(?<![a-z])(dos|tres|cuatro|cinco|seis|siete|two|three|four|five|six|seven)\s+(dias?|days?)(?![a-z])/i;
const _SEVERAL_DAYS_PATTERN = /(?<![a-z])(varios dias|several days)(?![a-z])/i;
const _MULTI_CITY_COUNT_PATTERN = /(?<![a-z])\d+\s*(ciudades|cities)(?![a-z])/i;
const _MULTI_CITY_PHRASES = ["varias ciudades", "multi-city", "multi city", "multiciudad"];
const _TRANSIT_LAYOVER_PHRASES = ["layover", "stopover", "escala", "en transito", "en tránsito", "tour de transito", "tour de tránsito"];
// Any day-count (including "1 día") followed by "desde/from" ANY place
// within a short window — the OPPOSITE direction from _isOutwardFromCity
// (e.g. "Ice hotel & Quebec City 1 día desde Montreal": Montreal, not the
// requested city, is the origin) — a day-count-shaped package that departs
// from somewhere else entirely is an excursion regardless of which city is
// the origin vs. destination.
const _DAY_COUNT_DESDE_PATTERN = /(?<![a-z0-9])\d+\s*[-]?\s*(dias?|days?)\b[^.;:]{0,30}\b(desde|from)(?![a-z])/i;

function _foldForMatch(text: string): string {
  return text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

/** True when the requested city is named as the origin of a "desde/from"
 *  route — the title-visible signal that a product leaves the city rather
 *  than staying in it (e.g. "Explora los Cotswolds desde Londres"). */
function _isOutwardFromCity(foldedTitle: string, foldedCityNames: readonly string[]): boolean {
  return foldedCityNames.some(city => {
    if (!city) return false;
    return foldedTitle.includes(`desde ${city}`) || foldedTitle.includes(`from ${city}`);
  });
}

/** Lightweight, title-only excursion heuristic — see module doc above for
 *  why this exists alongside (not instead of) marketplace_relevance_class. */
function _looksLikeExcursion(title: string, cityNames: readonly string[]): boolean {
  const folded = _foldForMatch(title);
  const foldedCityNames = cityNames.map(_foldForMatch);
  if (_isOutwardFromCity(folded, foldedCityNames)) return true;
  if (_DAY_COUNT_DESDE_PATTERN.test(folded)) return true;
  if (_MULTI_DAY_COUNT_PATTERN.test(folded)) return true;
  if (_WRITTEN_MULTI_DAY_PATTERN.test(folded)) return true;
  if (_SEVERAL_DAYS_PATTERN.test(folded)) return true;
  if (_MULTI_CITY_COUNT_PATTERN.test(folded)) return true;
  if (_MULTI_CITY_PHRASES.some(p => folded.includes(p))) return true;
  if (_TRANSIT_LAYOVER_PHRASES.some(p => folded.includes(p))) return true;
  return false;
}

/**
 * Single source of truth for "is this provider item an excursion/multi-day/
 * multi-city/transit product, or a core city experience" — used by
 * groupMarketplaceByIntent() below and safe to call directly wherever a
 * component needs the same answer for one item (e.g. a card's "Excursión"
 * label) without re-grouping the whole list. Only meaningful for provider
 * items — always false for local/reference items (they're a separate
 * bucket, see groupMarketplaceByIntent).
 */
export function isExcursionItem(exp: ExperienceItem, cityNames: readonly string[] = []): boolean {
  if (exp.source_type !== "provider") return false;
  const relevanceClass = exp.marketplace_relevance_class;
  if (typeof relevanceClass === "string" && _EXCURSION_RELEVANCE_CLASSES.has(relevanceClass)) return true;
  return _looksLikeExcursion(exp.title, cityNames);
}

/**
 * Subtle label text for an item already known to be an excursion (call
 * isExcursionItem() first — this doesn't re-check). "Viaje de varios días"
 * when the title's own day-count or the item's duration implies more than
 * one day; the plainer "Excursión" otherwise (day trip, multi-city without
 * an explicit day count, or a transit/layover tour).
 */
export function getExcursionLabel(exp: ExperienceItem): string {
  const folded = _foldForMatch(exp.title || "");
  const isMultiDay = _MULTI_DAY_COUNT_PATTERN.test(folded)
    || _WRITTEN_MULTI_DAY_PATTERN.test(folded)
    || _SEVERAL_DAYS_PATTERN.test(folded)
    || (typeof exp.duration_hours === "number" && exp.duration_hours > 24);
  return isMultiDay ? "Viaje de varios días" : "Excursión";
}

/**
 * Splits an already-built (and, when relevant, already-sorted) marketplace
 * item list into three display groups. Filtering preserves relative order,
 * so whatever sort produced `items` is preserved within each group — no
 * separate per-group re-sort is needed.
 *
 * cityNames: the requested destination's own display/name variants (e.g.
 * ["Quebec City", "Quebec"]), used only for the "desde {city}" heuristic.
 * Safe to omit — that one signal just won't fire without it.
 *
 * Never mutates any ExperienceItem — purely a display-layer partition.
 */
export function groupMarketplaceByIntent(
  items:     MarketplaceItem[],
  cityNames: readonly string[] = [],
): MarketplaceIntentGroups {
  const cityExperiences: MarketplaceItem[] = [];
  const excursions:      MarketplaceItem[] = [];
  const localReferences: MarketplaceItem[] = [];

  for (const item of items) {
    const { exp } = item;
    if (exp.source_type !== "provider") {
      localReferences.push(item);
      continue;
    }
    if (isExcursionItem(exp, cityNames)) {
      excursions.push(item);
    } else {
      cityExperiences.push(item);
    }
  }

  return { cityExperiences, excursions, localReferences };
}

/**
 * Picks at most one small editorial "shelf" — a themed preview, not a
 * partition — to surface above the main sectioned list. Tries a time-of-day
 * concentration first (e.g. "Buenas para la mañana"), then the single most
 * common category (e.g. "Gastronomía"). Returns null when nothing in the
 * inventory is concentrated enough to be worth calling out. Items shown here
 * still appear again in the main list below — this is a highlight, not a move.
 */
export function pickThemedTeaser(items: MarketplaceItem[]): MarketplaceSection | null {
  const morning = items.filter(i => i.exp.time_of_day === "morning");
  if (morning.length >= MIN_SECTION_SIZE) {
    return { label: "Buenas para la mañana", items: morning.slice(0, 3) };
  }

  const counts = new Map<string, number>();
  for (const item of items) counts.set(item.exp.category, (counts.get(item.exp.category) ?? 0) + 1);
  let bestCategory: string | null = null;
  let bestCount = 0;
  for (const [category, count] of counts) {
    if (count > bestCount) { bestCategory = category; bestCount = count; }
  }
  if (bestCategory && bestCount >= MIN_SECTION_SIZE) {
    const label = CATEGORY_META[bestCategory]?.label ?? bestCategory;
    return { label, items: items.filter(i => i.exp.category === bestCategory).slice(0, 3) };
  }

  return null;
}

// ─── Inventory depth framing ────────────────────────────────────────────────
// Honest contextual copy about how much inventory a destination actually has
// — never fakes extra volume, just frames the real count so a thin catalog
// reads as "we're growing" rather than "something is broken". Based on the
// destination's total (unfiltered) count, not the currently-filtered count,
// so applying a filter doesn't make the destination look thinner than it is.

export interface InventoryDepthCopy {
  headline: string;
  detail:   string;
}

export function getInventoryDepthCopy(total: number): InventoryDepthCopy {
  if (total >= 20) {
    return {
      headline: "Amplia selección disponible",
      detail:   "Explora experiencias por categoría, momento y proveedor.",
    };
  }
  if (total >= 10) {
    return {
      headline: "Selección disponible para comparar",
      detail:   "Puedes filtrar por categoría, duración y proveedor.",
    };
  }
  return {
    headline: "Inventario limitado por ahora",
    detail:   "Estamos ampliando experiencias para este destino.",
  };
}

// ─── Coverage-tier banner (Launch Conversion UX V1, task 4) ─────────────────
// Honest supplier-status framing, distinct from the inventory-depth copy
// above (which is about VOLUME) — this is about how much of that volume is
// real, bookable provider supply vs. local/reference content. Mirrors
// engine/supply_integration/coverage_tier.py's tier names exactly; never
// invents a tier or infers one client-side beyond the explicit
// provider_count === 0 fallback (for older cached responses that predate
// coverage_tier but still carry provider_count).

export type CoverageTierBannerKind = "reference_only" | "thin_provider_supply";

export interface CoverageTierBanner {
  kind:    CoverageTierBannerKind;
  message: string;
}

export function getCoverageTierBanner(
  coverageTier:  string | null | undefined,
  providerCount: number,
): CoverageTierBanner | null {
  // Provider-Only Marketplace & Comparison V2 Foundation, Part B #7/#8 —
  // once local references are hidden by default, the old mixed-view copy
  // (below, still used when the flag is on) is actively misleading: those
  // local items aren't shown to the user at all. The launch-mode message
  // stays just as honest as before — no overclaiming, no false certainty —
  // it simply never mentions local content the user can't see.
  const showLocal = isShowLocalReferencesEnabled();
  if (providerCount === 0 || coverageTier === "reference_only") {
    return {
      kind:    "reference_only",
      message: showLocal
        ? "Referencias locales disponibles · proveedores en expansión para este destino."
        : "Todavía no encontramos experiencias reservables para este destino. Estamos ampliando la cobertura de proveedores. Prueba otro destino o vuelve más adelante.",
    };
  }
  if (coverageTier === "thin_provider_supply") {
    return {
      kind:    "thin_provider_supply",
      message: showLocal
        ? "Pocas experiencias reservables por ahora · referencias locales incluidas."
        : "Pocas experiencias reservables por ahora para este destino.",
    };
  }
  return null;
}
